import { Link } from "react-router-dom";

function LegalLayout({ title, updated, children }) {
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <header className="border-b border-[#1E2A3F] sticky top-0 z-40 bg-[#0B0F17]/85 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center transition-opacity hover:opacity-80">
            <img src="/linkviewer-logo.png" alt="LinkViewer.PRO — home" className="h-12 w-auto" />
          </Link>
          <Link to="/" className="text-sm text-slate-300 hover:text-white">Back to home</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h1 className="font-display text-4xl font-bold tracking-tight mb-2">{title}</h1>
        <p className="text-xs text-slate-500 font-mono-niq mb-10">Last updated: {updated}</p>
        <div className="space-y-6 text-slate-300 text-sm leading-relaxed [&_h2]:font-display [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-white [&_h2]:mt-8 [&_h2]:mb-2 [&_p]:text-slate-400 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:text-slate-400 [&_a]:text-blue-400">
          {children}
        </div>
      </main>
      <footer className="border-t border-[#1E2A3F] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-3 text-xs text-slate-500">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div>2026 © LinkViewer.PRO. All rights reserved.</div>
            <div className="flex gap-4">
              <Link to="/terms" className="hover:text-slate-300">Terms</Link>
              <Link to="/privacy" className="hover:text-slate-300">Privacy</Link>
              <Link to="/refund" className="hover:text-slate-300">Refund</Link>
            </div>
          </div>
          <div className="text-slate-600 text-center sm:text-left">LinkedIn is a trademark of LinkedIn Corporation. LinkViewer.PRO is an independent tool and is not affiliated with, endorsed by or sponsored by LinkedIn.</div>
        </div>
      </footer>
    </div>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="June 2026">
      <p>Welcome to LinkViewer.PRO. These Terms of Service ("Terms") govern your access to and use of the LinkViewer.PRO website and application (the "Service"). By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.</p>

      <h2>1. The Service</h2>
      <p>LinkViewer.PRO lets you upload your own contacts file (CSV, Excel or ZIP) and generates an AI-assisted, searchable, categorized view of that data, including inferred attributes and exportable reports. All analysis is derived only from the file you upload. We do not scrape any third-party source.</p>

      <h2>2. Eligibility & Accounts</h2>
      <ul>
        <li>You must be at least 18 years old and able to form a binding contract.</li>
        <li>You are responsible for the activity under your account and for keeping your password secure.</li>
        <li>You must provide accurate information and keep it up to date.</li>
      </ul>

      <h2>3. Your Data & Acceptable Use</h2>
      <p>You represent that you have the right to upload the data you provide and that doing so does not violate any law or agreement (including the terms of the service you exported it from). You agree to use the Service only for lawful purposes and not to upload data you are not authorized to process. You are solely responsible for how you use any output.</p>
      <p>LinkViewer.PRO works exclusively on a CSV, Excel or ZIP file that you already own and obtained from a legitimate source you have the right to use. It performs <strong>no web browsing, no data scraping, and no external lookups of any kind</strong>, and it makes no online operations against any third party. Its only function is to read the file you upload and apply keyword-based categorization (labelling rows by attributes such as sector, seniority and region). It is <strong>not</strong> a data-scraping, lead-generation, or outbound-marketing tool: it does not collect or fetch new data, build prospecting or marketing lists, or send any messages on your behalf. You agree not to use the Service for unsolicited outbound marketing, cold outreach, or any activity that would violate the privacy rights of the people in your file or applicable anti-spam laws.</p>

      <h2>4. Free Trial & Subscriptions</h2>
      <ul>
        <li>Free accounts can upload one dataset, build a dashboard, and run a limited number of searches (view-only, no downloads).</li>
        <li>A paid subscription unlocks unlimited searches, unlimited datasets and PDF/Excel downloads.</li>
        <li>Subscriptions renew automatically until cancelled. You may cancel auto-renewal at any time from your account; access continues until the end of the current billing period.</li>
        <li>Except where required by law, payments are non-refundable. Prices may change with notice.</li>
      </ul>

      <h2>5. AI Output Disclaimer</h2>
      <p>Inferred fields (such as sector, seniority and region) and match rankings are automated estimates and may be inaccurate. They are provided "as is" for convenience and should not be relied upon as fact. Confidence labels indicate our estimate of certainty, not a guarantee.</p>

      <h2>6. Intellectual Property & Trademarks</h2>
      <p>The Service, including its software, design and branding, is owned by LinkViewer.PRO and protected by law. You retain ownership of the data you upload and the reports you export. LinkedIn is a trademark of LinkedIn Corporation; any reference to it is purely descriptive of the file format the Service can read. LinkViewer.PRO is an independent product and is not affiliated with, endorsed by, or sponsored by LinkedIn Corporation.</p>

      <h2>7. Termination</h2>
      <p>We may suspend or terminate your access if you breach these Terms or misuse the Service. You may stop using the Service and delete your data at any time.</p>

      <h2>8. Disclaimers & Limitation of Liability</h2>
      <p>The Service is provided "as is" and "as available" without warranties of any kind. To the maximum extent permitted by law, LinkViewer.PRO is not liable for any indirect, incidental or consequential damages, and our total liability will not exceed the amount you paid us in the twelve months preceding the claim.</p>

      <h2>9. Governing Law</h2>
      <p>These Terms are governed by the laws of the United States and the applicable state in which LinkViewer.PRO is established, without regard to conflict-of-law principles.</p>

      <h2>10. Contact</h2>
      <p>Questions about these Terms? Reach us through the in-app support chat.</p>
    </LegalLayout>
  );
}

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 2026">
      <p>This Privacy Policy explains how LinkViewer.PRO ("we", "us") collects, uses and protects your information when you use our website and application (the "Service").</p>

      <h2>1. Information We Collect</h2>
      <ul>
        <li><strong>Account information:</strong> your name and email address, and a securely hashed password (for email sign-in).</li>
        <li><strong>Uploaded data:</strong> the contacts file (CSV, Excel or ZIP) you choose to upload, and the enriched analysis derived from it.</li>
        <li><strong>Usage data:</strong> basic technical information such as IP address and activity needed to operate the Service, prevent abuse and manage free-trial eligibility.</li>
        <li><strong>Support messages:</strong> the name, email and message you send through our support chat.</li>
      </ul>

      <h2>2. How We Use Information</h2>
      <ul>
        <li>To provide, maintain and improve the Service.</li>
        <li>To generate the AI analysis and reports you request.</li>
        <li>To process subscriptions and payments.</li>
        <li>To respond to your support requests and prevent fraud or abuse.</li>
      </ul>

      <h2>3. AI Processing</h2>
      <p>To classify your contacts, non-identifying fields (such as company and job title) may be sent to third-party AI providers strictly to generate the analysis. We do not sell your data and we do not use it to train third-party models where we can control that setting.</p>

      <h2>4. Payments</h2>
      <p>Payments and subscriptions are processed by Paddle.com, which acts as our Merchant of Record and reseller. Paddle handles billing, payment methods and applicable taxes under its own <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noreferrer">Buyer Terms</a> and privacy policy. We do not store or process your full card details.</p>

      <h2>5. Data Retention & Deletion</h2>
      <p>Your uploaded datasets are stored under your account until you delete them. You can delete any dataset at any time from the Import page, and you may request deletion of your account and associated data through support.</p>

      <h2>6. Security</h2>
      <p>We use industry-standard measures such as password hashing, encrypted transport (HTTPS) and access controls. No method of transmission or storage is 100% secure, but we work to protect your information.</p>

      <h2>7. Your Rights</h2>
      <p>Depending on your location, you may have rights to access, correct, export or delete your personal data. Contact us through the support chat to exercise these rights.</p>

      <h2>8. Children</h2>
      <p>The Service is not directed to children under 18 and we do not knowingly collect their data.</p>

      <h2>9. Changes</h2>
      <p>We may update this policy from time to time. Material changes will be reflected by the "Last updated" date above.</p>

      <h2>10. Contact</h2>
      <p>For privacy questions, reach us through the in-app support chat.</p>
    </LegalLayout>
  );
}

export function Refund() {
  return (
    <LegalLayout title="Refund & Cancellation Policy" updated="June 2026">
      <p>This Refund & Cancellation Policy explains how billing, cancellations and refunds work for LinkViewer.PRO subscriptions. Our payments are handled by Paddle.com, which acts as our Merchant of Record.</p>

      <h2>1. Subscriptions & Billing</h2>
      <ul>
        <li>Pro is offered as a monthly ($12/month) or annual ($99/year) subscription.</li>
        <li>Subscriptions renew automatically at the end of each billing period until cancelled.</li>
        <li>Payment, invoicing and applicable taxes are processed by Paddle as reseller/Merchant of Record.</li>
      </ul>

      <h2>2. Cancelling Your Subscription</h2>
      <ul>
        <li>You can turn off auto-renewal at any time from your <strong>Account → Subscription</strong> page.</li>
        <li>When you cancel, you keep full Pro access until the end of the current billing period; you are not charged again after that.</li>
        <li>Cancelling stops future renewals — it does not, by itself, trigger a refund of the current period.</li>
      </ul>

      <h2>3. Refunds</h2>
      <ul>
        <li><strong>14-day guarantee:</strong> if you are not satisfied, you may request a full refund within 14 days of your first payment for a new subscription.</li>
        <li>Renewal payments (after the first period) are generally non-refundable, except where required by applicable law or consumer-protection rules.</li>
        <li>Refunds are issued to the original payment method via Paddle, typically within 5–10 business days.</li>
      </ul>

      <h2>4. How to Request a Refund</h2>
      <p>Contact us through the in-app support chat with your account email, or reply to the Paddle receipt you received by email. Paddle can also be contacted directly at <a href="https://paddle.net" target="_blank" rel="noreferrer">paddle.net</a> with your order details. We aim to respond to all requests within 2 business days.</p>

      <h2>5. Contact</h2>
      <p>Questions about billing or refunds? Reach us any time through the in-app support chat.</p>
    </LegalLayout>
  );
}
